<template>
  <div>
    <v-toolbar color="page-toolbar">
      <v-toolbar-title class="text-body-large">
        Bienvenue <span v-if="user">, {{ user.displayName }}</span>
      </v-toolbar-title>
    </v-toolbar>
    <v-row>
      <v-col cols="12"></v-col>
      <v-col cols="12" v-if="!isAuthResolved">
        <v-card class="mt-4 ml-2 mr-2" elevation="4">
          <v-skeleton-loader type="heading, paragraph, actions" loading-text="Chargement de votre compte..."></v-skeleton-loader>
        </v-card>
      </v-col>
      <v-col cols="12" v-if="isAuthResolved && !isConnected && !user">
        <v-card class="mt-4 ml-2 mr-2 hero-card" elevation="8">
          <div class="hero-bg" :style="heroBackgroundStyle"></div>
          <v-card-text class="pa-6 pa-md-8 hero-content">
            <!-- Brand lockup with the tagline of the brand sheet (docs/BRAND.md) -->
            <div class="hero-brand mb-4">
              <img src="@/assets/brand/logo.svg" alt="" class="hero-brand__logo" width="56" height="56" />
              <div>
                <div class="hero-brand__name">
                  {{ siteName.lead }} <span class="hero-brand__accent">{{ siteName.accent }}</span>
                </div>
                <div class="hero-brand__tagline">
                  Niveaux <em>·</em> Rivières <em>·</em> Cartes
                </div>
              </div>
            </div>
            <!-- The tagline always fits on one line: its size follows the card width (see .hero-title__main) -->
            <h1 class="hero-title mb-3">
              <span class="hero-title__lead">Info débit</span>
              <span class="hero-title__main">La référence des rivières sportives du Québec</span>
            </h1>
            <p class="text-body-large mb-5 hero-description">
              Consultez l'<strong>info-débit</strong> en temps réel : niveaux, tendances et recommandations de débit
              pour planifier vos sorties sur les rivières sportives du Québec.
            </p>

            <div class="hero-actions mb-6">
              <v-btn color="primary" size="large" :to="{ name: 'connexion' }">Connexion</v-btn>
              <!-- The screen's one gold call-to-action (docs/BRAND.md) -->
              <v-btn variant="flat" color="gold" size="large" :to="{ name: 'nouvel-utilisateur' }">Créer un compte</v-btn>
              <v-btn variant="outlined" color="primary" size="large" :to="{ name: 'rivieres' }">Explorer les rivières</v-btn>
            </div>

            <v-row density="compact">
              <v-col cols="12" md="4">
                <div class="hero-feature">
                  <v-icon color="primary" size="small">mdi-chart-line</v-icon>
                  <span>Recommandations de débits minimum et maximum par section</span>
                </div>
              </v-col>
              <v-col cols="12" md="4">
                <div class="hero-feature">
                  <v-icon color="primary" size="small">mdi-map-marker-path</v-icon>
                  <span>Rivières classées par région pour préparer vos déplacements</span>
                </div>
              </v-col>
              <v-col cols="12" md="4">
                <div class="hero-feature">
                  <v-icon color="primary" size="small">mdi-image-multiple-outline</v-icon>
                  <span>Contenu enrichi avec photos, vidéos et infos terrain</span>
                </div>
              </v-col>
            </v-row>

            <p class="mt-5 mb-0">
              En savoir plus sur notre approche de l'<router-link :to="{ name: 'info-debit' }">info-débit</router-link>
              ou consulter
              <router-link :to="{ name: 'a-propos' }">la page À propos</router-link>.
            </p>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <active-dangers-card class="mx-2" @loaded="dangersLoaded = true"></active-dangers-card>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <liked-sections-table
          v-if="isConnected"
          :authUser="user"
          :isMember="isMember"
          :likedIds="likedIds"
          @liked-changed="handleLikedChanged"
        />
      </v-col>
    </v-row>
  </div>
</template>

<script>
  import { auth } from "@/firebase";
  import { UserService } from "@/store/modules/user";
  import ActiveDangersCard from "@/components/pages/river/components/ActiveDangersCard.vue";
  import LikedSectionsTable from "@/components/pages/river/components/LikedSectionsTable.vue";
  import { applySeoMeta, buildHomeStructuredData, buildRouteSeo, signalPrerenderReady } from "@/utils/seo";
  import { getSiteName } from "@/services/SiteName.service";

  export default {
    components: { ActiveDangersCard, LikedSectionsTable },
    props: {
      isMember: Boolean,
      membershipEndDate: Number,
      isAdmin: Boolean,
      isConnected: Boolean,
      isAuthResolved: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        loading: false,
        user: null,
        likedIds: [], // Array to store liked section IDs
        heroBackgroundImage: `${process.env.BASE_URL}images/home-hero.jpg`,
        prerenderReadySignaled: false,
        dangersLoaded: false,
      };
    },
    computed: {
      siteName() {
        return getSiteName();
      },
      heroBackgroundStyle() {
        return {
          backgroundImage: `linear-gradient(var(--qww-hero-veil-top), var(--qww-hero-veil-bottom)), url(${this.heroBackgroundImage})`,
        };
      },
    },
    watch: {
      isAuthResolved: {
        immediate: true,
        handler(isResolved) {
          if (isResolved) {
            this.applyHomeSeo();
            this.signalPageReady();
          }
        },
      },
      dangersLoaded() {
        this.signalPageReady();
      },
    },
    async mounted() {
      this.loading = true;
      auth.onAuthStateChanged((user) => {
        if (user) {
          this.user = user;
          this.fetchLikedIds(user.uid); // Fetch liked section IDs when user is authenticated
        } else {
          this.user = null;
          this.likedIds = []; // Reset likedIds if user is not authenticated
        }
      });

      this.loading = false;
    },
    methods: {
      handleLikedChanged({ sectionId, isLiked }) {
        this.likedIds = isLiked
          ? [...new Set([...this.likedIds, sectionId])]
          : this.likedIds.filter((id) => id !== sectionId);
      },
      applyHomeSeo() {
        applySeoMeta({
          ...buildRouteSeo(this.$route),
          structuredData: buildHomeStructuredData(),
        });
      },
      signalPageReady() {
        // Prerender once the active dangers are in the page too
        if (this.prerenderReadySignaled || !this.isAuthResolved || !this.dangersLoaded) {
          return;
        }

        this.$nextTick(() => {
          signalPrerenderReady();
          this.prerenderReadySignaled = true;
        });
      },
      async fetchLikedIds(userId) {
        try {
          const likedSectionsSnapshot = await UserService.actions.getLikedSections(userId);
          this.likedIds = likedSectionsSnapshot.docs.map((doc) => doc.id);
        } catch (error) {
          console.error("Error fetching liked section IDs:", error);
          this.likedIds = []; // Fallback to an empty array
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .hero-card {
    position: relative;
    overflow: hidden;
    border: 1px solid var(--qww-hero-border);
    background: linear-gradient(135deg, var(--qww-hero-bg-start) 0%, var(--qww-hero-bg-middle) 45%, var(--qww-hero-bg-end) 100%);
  }

  .hero-bg {
    position: absolute;
    inset: 0;
    background-size: cover;
    background-position: center;
    opacity: 0.75;
    filter: saturate(0.98) contrast(0.98);
    pointer-events: none;
  }

  .hero-content {
    position: relative;
    z-index: 1;
    /* The title sizes itself on this width (cqi units) */
    container-type: inline-size;
  }

  .hero-title {
    margin: 0;
    font-weight: 700;
    line-height: 1.15;
  }

  .hero-title__lead {
    display: block;
    margin-bottom: 4px;
    /* Always smaller than the sentence under it (4.6cqi) */
    font-size: clamp(0.75rem, 2.6cqi, 1.6rem);
    font-weight: 600;
    color: var(--qww-hero-kicker);
  }

  /* One line at every width: in bold Roboto the sentence is about 20.6em wide, so 4.6cqi fills about 95% of the
     card. The maximum keeps it from growing past the display size on wide screens. */
  .hero-title__main {
    display: block;
    white-space: nowrap;
    font-size: clamp(0.75rem, 4.6cqi, 3rem);
    letter-spacing: -0.01em;
  }

  .hero-brand {
    display: flex;
    align-items: center;
    gap: 14px;
  }

  .hero-brand__logo {
    display: block;
    flex: none;
  }

  .hero-brand__name {
    font-size: 1.75rem;
    font-weight: 800;
    letter-spacing: -0.5px;
    line-height: 1;
    color: var(--qww-hero-wordmark);
  }

  .hero-brand__accent {
    font-weight: 400;
    color: var(--qww-hero-kicker);
  }

  .hero-brand__tagline {
    margin-top: 6px;
    font-size: 0.75rem;
    font-weight: 500;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  }

  /* Gold separators, as on the brand sheet */
  .hero-brand__tagline em {
    font-style: normal;
    color: rgb(var(--v-theme-gold));
  }

  @media (max-width: 600px) {
    .hero-brand__logo {
      width: 44px;
      height: 44px;
    }

    .hero-brand__name {
      font-size: 1.4rem;
    }
  }

  .hero-description {
    max-width: 760px;
  }

  .hero-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
  }

  .hero-feature {
    display: flex;
    align-items: flex-start;
    gap: 0.45rem;
    line-height: 1.4;
  }

  @media (max-width: 960px) {
    .hero-actions {
      flex-direction: column;
      align-items: stretch;
    }
  }
</style>
